import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Button, Skeleton, Tooltip, Typography } from 'antd';
import classNames from 'classnames';
import { ClockCircleFilled } from '@ant-design/icons';
import { Routes } from 'router/routes';
import BaseEmpty from 'components/atoms/oneplatform/base-empty';

import styles from './styles.module.scss';
import NextAppointmentCountdown from './countdown';
import { getMethodTagClassName, renderMethodIcon } from '../utils/method-tag';

import type { INextAppointmentAction, INextAppointmentProps } from './types';

const { Text } = Typography;

const NextAppointment = memo(
  ({
    title,
    details,
    actionButtons,
    dateTimeHeading,
    startsInHeading,
    emptyMessage,
    countdownAriaLabelBuilder,
    onCountdownComplete,
    isLoading,
    isFetching,
    className,
  }: INextAppointmentProps) => {
    const navigate = useNavigate();
    const patientInitials = useMemo(() => {
      if (!details?.patient.name) return undefined;
      return details.patient.name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((segment) => segment[0]?.toUpperCase())
        .join('');
    }, [details?.patient.name]);

    const iconActions = useMemo(
      () => actionButtons?.filter((action) => !action.label) ?? [],
      [actionButtons],
    );
    const labeledActions = useMemo(
      () => actionButtons?.filter((action) => Boolean(action.label)) ?? [],
      [actionButtons],
    );

    const handleAction = useCallback(
      (action: INextAppointmentAction) => () => {
        if (!details || !action.onClick) return;
        action.onClick(details);
      },
      [details],
    );

    const handleLabeledActionClick = useCallback(() => {
      if (!details?.patient.guid) return;
      navigate(Routes.patientGuid(details.patient.guid));
    }, [details]);

    const renderMethodTag = () => {
      if (!details?.methodTag) return null;
      const { label, variant } = details.methodTag;
      return (
        <span
          className={classNames(
            styles['method-tag'],
            getMethodTagClassName(styles, variant),
          )}
        >
          {renderMethodIcon(variant, styles['method-icon'])}
          {label}
        </span>
      );
    };

    const sectionAriaLabel = title || 'Next appointment';

    const showSkeleton = Boolean(isLoading || isFetching);
    const showActions = Boolean(details && actionButtons?.length);

    return (
      <section
        className={classNames(styles['next-appointment'], className)}
        aria-label={sectionAriaLabel}
      >
        <header className={styles.header}>
          {title ? <Text className={styles.title}>{title}</Text> : null}
          {showActions ? (
            <div className={styles.actions}>
              {iconActions.map((action) => (
                <Tooltip
                  key={action.id}
                  title={action.tooltip}
                  placement="bottom"
                >
                  <Button
                    key={action.id}
                    type="default"
                    size="small"
                    aria-label={action.ariaLabel}
                    onClick={handleAction(action)}
                    disabled={!details}
                    className={styles['action-icon-button']}
                    icon={action.icon}
                  />
                </Tooltip>
              ))}
              {labeledActions.map((action) => (
                <Button
                  key={action.id}
                  type="primary"
                  size="small"
                  aria-label={action.ariaLabel || action.label}
                  onClick={handleLabeledActionClick}
                  disabled={!details}
                  className={styles['action-primary-button']}
                  icon={
                    action.icon ? (
                      <span className={styles['action-primary-icon']}>
                        {action.icon}
                      </span>
                    ) : undefined
                  }
                >
                  <span className={styles['action-primary-label']}>
                    {action.label}
                  </span>
                </Button>
              ))}
            </div>
          ) : null}
        </header>

        {showSkeleton && (
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        )}
        {!showSkeleton && details && (
          <div className={styles['details-card']}>
            <div className={styles['patient-row']}>
              <Avatar
                size={36}
                src={details.patient.avatarUrl}
                className={styles.avatar}
              >
                {patientInitials}
              </Avatar>
              <div className={styles['patient-info-group']}>
                <Text className={styles['patient-name']} ellipsis>
                  {details.patient.name}
                </Text>
                {details.patient.birthDateLabel ||
                details.patient.languageLabel ? (
                  <div className={styles['patient-meta']}>
                    {details.patient.birthDateLabel ? (
                      <span>{details.patient.birthDateLabel}</span>
                    ) : null}
                    {details.patient.birthDateLabel &&
                    details.patient.languageLabel ? (
                      <span
                        className={styles['meta-divider']}
                        aria-hidden="true"
                      >
                        |
                      </span>
                    ) : null}
                    {details.patient.languageLabel ? (
                      <span>{details.patient.languageLabel}</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {renderMethodTag()}
            </div>

            <div className={styles['info-grid']}>
              <div className={styles['info-column']}>
                {dateTimeHeading ? (
                  <Text className={styles['info-label']}>
                    {dateTimeHeading}
                  </Text>
                ) : null}
                <div className={styles['info-value']}>
                  <ClockCircleFilled className={styles['info-icon']} />
                  <Text className={styles['info-text']}>
                    {details.dateTimeLabel}
                  </Text>
                </div>
                <div className={styles['info-value']}>
                  <Text className={styles['sub-text']}>
                    (CST: {details.subDateTimeLabel})
                  </Text>
                </div>
              </div>
              {details?.appointmentDateTime || startsInHeading ? (
                <div
                  className={classNames(
                    styles['info-column'],
                    styles['info-column-right'],
                  )}
                >
                  {startsInHeading ? (
                    <Text className={styles['info-label']}>
                      {startsInHeading}
                    </Text>
                  ) : null}
                  <div
                    className={classNames(
                      styles['info-value'],
                      styles['starts-in-value'],
                    )}
                  >
                    <NextAppointmentCountdown
                      targetDate={details?.appointmentDateTime}
                      ariaLabelBuilder={countdownAriaLabelBuilder}
                      onComplete={onCountdownComplete}
                      textClassName={styles['info-text']}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
        {!showSkeleton && !details && <BaseEmpty description={emptyMessage} />}
      </section>
    );
  },
);

NextAppointment.displayName = 'NextAppointment';

export default NextAppointment;
